import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';

import { User } from '../../../../modules/authentication/models/v1-api.interface';
import { AuthenticationService } from '../../../../modules/authentication/services/authentication.service';
import { DashboardWrapperComponent } from '../../../../modules/dashboard-wrapper/dashboard-wrapper.component';
import { PipesModule } from '../../../../modules/pipes/pipes.module';
import { ICommissionIssuer } from '../../../../modules/shared/models/commission-issuer';
import { BrokerService } from '../../../../modules/shared/services/broker.service';
import { CommissionIssuerService } from '../../../../modules/shared/services/commission-issuer.service';
import { SharedService } from '../../../../modules/shared/services/shared.service';
import { SharedModule } from '../../../../modules/shared/shared.module';
import { brokerageFormValidator } from '../../../../modules/shared/validators/brokerage-form.validator';
import { BrokerageFormInterface } from '../../../../modules/signup/models/signup-form.interface';
import { IBroker } from '../../models/broker';
import { IPartner } from '../../models/partner';

@Component({
  selector: 'app-dashboard-broker',
  templateUrl: './dashboard-broker.component.html',
  styleUrls: ['./dashboard-broker.component.scss'],
  standalone: true,
  imports: [
    PipesModule,
    DashboardWrapperComponent,
    SharedModule,
    MatCardModule,
  ],
})
export class DashboardBrokerComponent implements OnInit, OnDestroy {
  public form = this.formBuilder.group(
    {
      id: [undefined],
      officePhone: [''],
      businessName: ['', [Validators.required]],
      agentLicenseNumber: ['', [Validators.required]],
      agentIsBroker: [undefined, [Validators.required]],
      commissionIssuerId: [undefined],
      commissionIssuerPhone: [''],
      commissionIssuerName: [''],
      commissionIssuerEmail: ['', [Validators.email]],
      completedTransactions: [0],
      pendingContracts: [0],
      activeListings: [0],
    },
    {
      validators: brokerageFormValidator(),
    },
  );

  public loading = true;
  public readonly = true;
  public broker: IBroker | undefined;
  public partners?: IPartner[];
  public commissionIssuer: ICommissionIssuer | undefined;
  public error = '';
  private user = this.authenticationService.profile;
  private backupForm: BrokerageFormInterface;
  private subscriptions: Array<Subscription> = [];

  constructor(
    private router: Router,
    private brokerService: BrokerService,
    private authenticationService: AuthenticationService,
    private sharedService: SharedService,
    private commissionIssuerService: CommissionIssuerService,
    private formBuilder: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.user.subscribe((user) => {
        if (!user) {
          return;
        }

        this.loading = true;
        this.loadBroker(user).finally(() => (this.loading = false));
        this.loadPartner(user).finally(() => (this.loading = false));
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private async loadBroker(user: User): Promise<void> {
    if (user.brokerId) {
      const broker = await firstValueFrom(
        this.brokerService.getBrokerById(user.brokerId),
      );
      this.broker = broker;
      this.form.patchValue({
        id: broker.id,
        officePhone: broker.officePhone || '',
        businessName: broker.businessName || '',
        agentLicenseNumber: user.licenseNumber || '',
        agentIsBroker: user.isBrokerAndAgent,
        completedTransactions: user.completedTransactions,
        pendingContracts: user.pendingContracts,
        activeListings: user.activeListings,
      });
    }

    if (user.commissionIssuerId) {
      const commissionIssuer = await firstValueFrom(
        this.commissionIssuerService.getById(user.commissionIssuerId),
      );
      this.commissionIssuer = commissionIssuer;
      this.form.patchValue({
        commissionIssuerId: commissionIssuer.id,
        commissionIssuerPhone: commissionIssuer.phoneNumber || '',
        commissionIssuerName: commissionIssuer.fullName || '',
        commissionIssuerEmail: commissionIssuer.email || '',
      });
    }

    this.backupForm = this.form.getRawValue();
  }

  private async loadPartner(user: User): Promise<void> {
    this.partners = await firstValueFrom(this.brokerService.getPartnersList());
  }

  public async save(): Promise<void> {
    const brokerage: BrokerageFormInterface = this.form.getRawValue();
    const diff = this.sharedService.getDiff(brokerage, this.backupForm);

    if (Object.keys(diff).length === 0) {
      this.router.navigate(['/dashboard']);
      return;
    }

    try {
      await this.brokerService
        .updateBrokerageInformation(brokerage)
        .toPromise();
      this.router.navigate(['/dashboard']);
    } catch (e) {
      this.error = this.sharedService.getErrorText(e, true);
    }
  }
}
